import { useApiQuery } from "@/hooks/use-api";
import {
  fetchSsoOverview,
  type ApiErrorInfo,
  type ApiSsoBreakGlassSummary,
  type ApiSsoProvider,
  type SsoOverviewResult,
} from "@/lib/api";

/**
 * List hook for the organization's identity providers, following the repo's
 * documented data-flow (AGENTS.md → "Data Flow: API → Hook → Component"):
 * `fetchSsoOverview` is the only source of rows, so an unreachable API produces
 * an explicit error or unavailable state rather than a seeded provider.
 *
 * `enabled` is the permission gate: the request is not issued at all until the
 * loaded effective-access contract carries the SSO permission, which is what
 * makes a machine or non-admin session see no controls *and* cause no traffic.
 */
const EMPTY_OVERVIEW = {
  providers: [] as ApiSsoProvider[],
  breakGlass: null as ApiSsoBreakGlassSummary | null,
};

function mapSsoOverviewResponse(raw: SsoOverviewResult) {
  return {
    data: { providers: raw.providers, breakGlass: raw.breakGlass },
    error: raw.error,
    // Never "empty falls back to mock": an organization with no provider must
    // render the honest empty state, not placeholder rows.
    isEmpty: false,
  };
}

export interface SsoProvidersState {
  providers: ApiSsoProvider[];
  breakGlass: ApiSsoBreakGlassSummary | null;
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

export function useSsoProviders(enabled: boolean): SsoProvidersState {
  const result = useApiQuery(fetchSsoOverview, mapSsoOverviewResponse, {
    initialData: EMPTY_OVERVIEW,
    enabled,
  });

  return {
    providers: result.data.providers,
    breakGlass: result.data.breakGlass,
    loading: result.loading,
    error: result.error,
    refresh: result.refresh,
  };
}
